import Cookies from 'js-cookie';

import { CallMethodEnums } from '@/types/api';
import { SESSION_COOKIE, SessionCookie } from '@/types/cookie';
import {
  Contract, ContractsListResponse, ContractStatusResponse, ContractValidateRes, TaskStatusResponse
} from '@/types/dataContracts';

import { callJson, executeFetch } from './utils';

const DCM_PREFIX = '/api/proxy/dcm/api/v1';

// FastAPI returns errors as `{ "detail": "..." }`. Extract the human-readable
// message instead of leaking the raw JSON envelope into UI toasts.
const errorMessageFromResponse = async( res: Response, fallback: string ): Promise<string> => {
  let text = '';
  try {
    text = await res.text();
  } catch {
    return fallback;
  }
  if ( !text ) return fallback;
  try {
    const json = JSON.parse( text );
    if ( typeof json?.detail === 'string' ) return json.detail;
    if ( typeof json?.detail?.message === 'string' ) return json.detail.message;
    if ( typeof json?.message === 'string' ) return json.message;
  } catch {
    // Not JSON — fall through to raw text
  }
  return text;
};

export const getContracts = async(): Promise<ContractsListResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContract = async( contractId: string ): Promise<Contract> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContractStatus = async( contractId: string ): Promise<ContractStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/status/${ contractId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const deleteContract = async( contractId: string ): Promise<TaskStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.DELETE,
    isProtected: true
  } );
};

export const validateContract = async( file: File ): Promise<ContractValidateRes> => {
  const formData = new FormData();
  formData.append( 'file', file );

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/validate_contract`,
    method: CallMethodEnums.POST,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Validation failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const createContract = async( file: File ): Promise<{ task_id: string; contract_id?: string }> => {
  const sessionCookieStr = Cookies.get( SESSION_COOKIE )?.valueOf();
  const sessionCookie: SessionCookie | undefined = sessionCookieStr ? JSON.parse( sessionCookieStr ) : undefined;

  const formData = new FormData();
  formData.append( 'file', file );
  if ( sessionCookie?.userId ) {
    formData.append( 'submitted_by', sessionCookie.userId );
  }

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/create_contract/`,
    method: CallMethodEnums.POST,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Create contract failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const updateContract = async( contractId: string, file: File ): Promise<{ task_id: string; contract_id?: string }> => {
  const formData = new FormData();
  formData.append( 'file', file );

  const res = await executeFetch( {
    route: `${ DCM_PREFIX }/contracts/${ contractId }`,
    method: CallMethodEnums.PUT,
    isProtected: true,
    formData
  } );

  if ( !res.ok ) {
    throw new Error( await errorMessageFromResponse( res, `Update contract failed with status ${ res.status }` ) );
  }

  return res.json();
};

export const getTaskStatus = async( taskId: string ): Promise<TaskStatusResponse> => {
  return callJson( {
    route: `${ DCM_PREFIX }/tasks/${ taskId }`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
};

export const getContractCountBySecret = async( secretName: string ): Promise<number> => {
  const res = await callJson<undefined, { count: number }>( {
    route: `${ DCM_PREFIX }/contracts/by-secret/${ encodeURIComponent( secretName ) }/count`,
    method: CallMethodEnums.GET,
    isProtected: true
  } );
  return res.count;
};
